// Model and polling helpers for Revert VM to Snapshot runs. See revertRuns.spec.md (RVR-FR-001..006)
import type { TaskTrayItem } from 'core/components/task-tray/TaskTray'
import { getVolumeEventsForVolume } from 'openstack/components/storage/volumes/actions'
import { getVmEventDetails, getVmEvents } from 'openstack/components/vms/actions'

export const POLL_INTERVAL_MS = 15_000
// RVR-FR-008: how long to wait for the rebuild to finish before giving up
export const REBUILD_TIMEOUT_MS = 15 * 60 * 1000
// Event of a VM 'rebuild' action whose result says whether the rebuild worked
export const REBUILD_EVENT_NAME = 'compute_rebuild_instance'

export type StepStatus = 'waiting' | 'inProgress' | 'completed' | 'failed'

export interface RevertRunVolume {
  id: string
  name: string
  snapshotId: string
  status: StepStatus
}

// One click of "Revert": the VM rebuild followed by the revert of each data volume
export interface RevertRun {
  id: string
  vmId: string
  vmName: string
  snapshotName: string
  rebuildStatus: StepStatus
  volumes: RevertRunVolume[]
  error: string | null
  startedAt: number
}

export type RevertRunOutcome = 'inProgress' | 'success' | 'attention' | 'failed'

// RVR-FR-001: the run is over once it failed, or the rebuild and every volume revert are done
export const getRunOutcome = (run: RevertRun): RevertRunOutcome => {
  if (run.error || run.rebuildStatus === 'failed') return 'failed'
  const volumeStatuses = run.volumes.map((volume) => volume.status)
  const settled = volumeStatuses.every((status) => status === 'completed' || status === 'failed')
  if (run.rebuildStatus !== 'completed' || !settled) return 'inProgress'
  const failed = volumeStatuses.filter((status) => status === 'failed').length
  if (!failed) return 'success'
  return failed === volumeStatuses.length ? 'failed' : 'attention'
}

export const isRunActive = (run: RevertRun): boolean => getRunOutcome(run) === 'inProgress'

// RVR-FR-002: the one line shown on the task tray card
export const trayItem = (run: RevertRun): TaskTrayItem => {
  const outcome = getRunOutcome(run)
  const total = run.volumes.length
  const failed = run.volumes.filter((volume) => volume.status === 'failed').length
  const done = run.volumes.filter((volume) => volume.status === 'completed').length
  const message = {
    inProgress:
      run.rebuildStatus === 'completed'
        ? `${run.vmName}: reverting data volumes (${done} of ${total} done)…`
        : `${run.vmName}: rebuilding the VM from ${run.snapshotName}…`,
    success: `${run.vmName} was reverted to ${run.snapshotName}.`,
    attention: `${run.vmName}: ${failed} of ${total} volumes failed to revert.`,
    failed: run.error ? `${run.vmName}: ${run.error}` : `${run.vmName}: the revert failed.`,
  }[outcome]
  return {
    id: run.id,
    status: outcome,
    message,
    label: `Revert ${run.vmName} to snapshot`,
    actionLabel: 'Show details',
  }
}

export const hasTimedOut = (startedAt: number, timeoutMs: number, now = Date.now()): boolean =>
  now - startedAt >= timeoutMs

export const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

const newestFirst = (key: string) => (a: any, b: any) =>
  new Date(b[key]).getTime() - new Date(a[key]).getTime()

// RVR-FR-003: Nova task states while a rebuild runs: rebuilding, rebuild_block_device_mapping,
// rebuild_spawning
export const isRebuilding = (vm: any): boolean =>
  String(vm?.['OS-EXT-STS:task_state'] ?? '').startsWith('rebuild')

export type RebuildResult = 'success' | 'failed' | 'inProgress'

// RVR-FR-003: result of the REBUILD_EVENT_NAME event of the newest 'rebuild' VM event. 'Success'
// and 'Error' are final; an empty result, or an event that is not there yet, means the rebuild is
// still going (Nova clears the task state a moment before it writes the event result).
export const getLatestRebuildResult = async (vmId: string): Promise<RebuildResult> => {
  const events = await getVmEvents({ id: vmId })
  const [latestRebuild] = (events ?? [])
    .filter((event: any) => event.action === 'rebuild')
    .sort(newestFirst('start_time'))
  if (!latestRebuild) return 'inProgress'
  const details = await getVmEventDetails({ vmId, requestId: latestRebuild.request_id })
  const rebuildEvent = details?.events?.find((event: any) => event.event === REBUILD_EVENT_NAME)
  const result = String(rebuildEvent?.result ?? '').toLowerCase()
  if (!result) return 'inProgress'
  return result === 'success' ? 'success' : 'failed'
}

const getRevertEvents = async (volumeId: string) => {
  const events = await getVolumeEventsForVolume({ volumeId })
  return (Array.isArray(events) ? events : []).filter(
    (event) => event.action === 'revert_to_snapshot',
  )
}

// RVR-FR-004: revert events recorded before this run, so they are not mistaken for its own
export const getRevertRequestIds = async (volumeId: string) =>
  new Set((await getRevertEvents(volumeId)).map((event) => event.request_id))

// RVR-FR-004: status of the latest revert_to_snapshot event of this run
export const getRevertStatus = async (
  volumeId: string,
  ignoredRequestIds: Set<string>,
): Promise<StepStatus> => {
  const [latest] = (await getRevertEvents(volumeId))
    .filter((event) => !ignoredRequestIds.has(event.request_id))
    .sort(newestFirst('last_ts'))
  if (latest?.result === 'succeeded') return 'completed'
  if (latest?.result === 'failed') return 'failed'
  return 'inProgress'
}

// RVR-FR-009: whether the newest revert_to_snapshot event that started at or after `since` failed
export const getRevertFailedSince = async (volumeId: string, since: string): Promise<boolean> => {
  const sinceMs = new Date(since).getTime()
  const [latest] = (await getRevertEvents(volumeId))
    .filter((event) => new Date(event.first_ts).getTime() >= sinceMs)
    .sort(newestFirst('first_ts'))
  return latest?.result === 'failed'
}

// Status of the "Revert data volumes" step, derived from its volumes
export const getRevertStepStatus = (run: RevertRun): StepStatus => {
  const statuses = run.volumes.map((volume) => volume.status)
  if (statuses.includes('failed')) return 'failed'
  if (statuses.every((status) => status === 'completed')) return 'completed'
  return run.rebuildStatus === 'completed' ? 'inProgress' : 'waiting'
}
